import * as React from 'react';
import {
  Typography,
  Box,
  Pagination,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import ActionDialog from '../../app/components/ActionDialog';
import { getPartners, deletePartner } from '../../app/store/actions/adminActions';
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformButton,
  PlatformEmptyState,
  PlatformLoadingBlock,
  PlatformSearchBar,
  PlatformSearchRow,
  PlatformAvatar,
  PlatformBadge,
  getInitials,
  pickAvatarPalette,
} from '../../app/components/platform';
import {
  platformTableSx,
  platformTableContainerSx,
  platformColors,
  platformFonts,
} from '../../app/components/tickitPlatformTheme';
import AddIcon from '@mui/icons-material/Add';

function PartnerTableRow({ partner, navigate, onDelete }) {
  const palette = pickAvatarPalette(partner._id || partner.name);
  const eventCount = partner.events?.length ?? 0;

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <PlatformAvatar
            initials={getInitials(partner.name)}
            bg={palette.bg}
            color={palette.color}
            size={34}
          />
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.8125rem',
                color: 'var(--platform-text)',
                fontFamily: platformFonts.body,
              }}
            >
              {partner.name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--platform-text-3)', fontFamily: platformFonts.body }}>
              {partner.email}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <PlatformBadge variant="purple">RSVP & events</PlatformBadge>
      </TableCell>
      <TableCell>
        <Typography sx={{ fontWeight: 700, color: 'var(--platform-text)', fontFamily: platformFonts.body }}>
          {eventCount}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ fontFamily: platformFonts.body, color: 'var(--platform-text-2)' }}>—</Typography>
      </TableCell>
      <TableCell>
        <PlatformBadge variant="green">Active</PlatformBadge>
      </TableCell>
      <TableCell>
        <PlatformBadge variant="blue">Standard</PlatformBadge>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
          <PlatformButton
            variant="ghost"
            size="small"
            onClick={() => navigate(`/Super/Partner/edit/${partner._id}`)}
          >
            Manage
          </PlatformButton>
          <PlatformButton variant="ghost" size="small" onClick={onDelete}>
            Delete
          </PlatformButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export const SearchPartners = ({ partners, getPartners, deletePartner }) => {
  const navigate = useNavigate();

  const initialState = {
    search: "",
    page: 1,
    limit: 10,
  };

  React.useEffect(() => {
    getPartners(initialState);
  }, []);

  const [formData, setFormData] = React.useState(initialState);
  const [actionDialog, setActionDialog] = React.useState(false);
  const [action, setAction] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  React.useEffect(() => {
    getPartners(formData);
  }, [action]);

  const { search, page } = formData;
  const partnerDocs = partners?.results?.partners?.docs || [];
  const totalPartners = partners?.results?.partners?.totalDocs ?? partnerDocs.length;

  const handlePageChange = (event, value) => {
    const next = { ...formData, page: value };
    setFormData(next);
    getPartners(next);
  };

  const handleSearchChange = (event) => {
    setFormData({ ...formData, search: event.target.value });
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchFilter();
    }
  };

  const handleSearchFilter = () => {
    getPartners({ ...formData, page: 1 });
    setFormData({ ...formData, page: 1 });
  };

  const handleClearFilter = () => {
    setFormData(initialState);
    getPartners(initialState);
  };

  const handleDeletePartner = async () => {
    if (!deleteTarget) return;
    await deletePartner({ partnerId: deleteTarget._id });
    setActionDialog(false);
    setDeleteTarget(null);
    setAction((a) => !a);
  };

  return (
    <PlatformPage>
      <PlatformPageHeader
        title="Partner accounts"
        subtitle="Search and manage partner organizations on Tickit"
        actions={
          <PlatformButton
            variant="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/Super/Partner/create')}
          >
            New partner
          </PlatformButton>
        }
      />

      <PlatformCard title="Search partners" sx={{ mb: 2.5 }}>
        <PlatformSearchRow sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 0 }}>
          <PlatformSearchBar
            value={search}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            placeholder="Search partners by name..."
          />
          <PlatformButton variant="ghost" onClick={handleClearFilter}>
            Clear
          </PlatformButton>
          <PlatformButton variant="primary" onClick={handleSearchFilter}>
            Search
          </PlatformButton>
        </PlatformSearchRow>
      </PlatformCard>

      <Typography
        sx={{
          fontFamily: platformFonts.body,
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: 'var(--platform-text-2)',
          mb: 1.5,
        }}
      >
        {partners?.isLoading ? 'Loading…' : `${totalPartners} partner${totalPartners === 1 ? '' : 's'}`}
      </Typography>

      <PlatformCard title="Partner accounts" flush>
        {!partners?.isLoading ? (
          partnerDocs.length > 0 ? (
            <TableContainer sx={platformTableContainerSx}>
              <Table sx={{ minWidth: 720, ...platformTableSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Module</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Invitations</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partnerDocs.map((partner) => (
                    <PartnerTableRow
                      key={partner._id}
                      partner={partner}
                      navigate={navigate}
                      onDelete={() => {
                        setDeleteTarget(partner);
                        setActionDialog(true);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PlatformEmptyState title="No partners found" description="Try adjusting your search filters." />
          )
        ) : (
          <PlatformLoadingBlock />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination
            count={partners?.results?.partners?.pages || 1}
            page={page}
            onChange={handlePageChange}
            sx={{ '& .Mui-selected': { bgcolor: `${platformColors.brandPale} !important`, color: platformColors.brand } }}
          />
        </Box>
      </PlatformCard>

      <ActionDialog
        open={actionDialog}
        handleClose={() => {
          setActionDialog(false);
          setDeleteTarget(null);
        }}
        handleAccept={handleDeletePartner}
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete partner?'}
        content="Deleting this partner is irreversible."
      />
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  partners: state?.records,
});

const mapDispatchToProps = { getPartners, deletePartner };

export default connect(mapStateToProps, mapDispatchToProps)(SearchPartners);
